import React, { Ref, forwardRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown as DropdownOriginal, DropdownProps as DropdownPropsOriginal } from 'primereact/dropdown';
import { IconType, classNames } from 'primereact/utils';
import { SelectItem } from 'primereact/selectitem';
import { Tooltip } from 'components/_new/Tooltip/Tooltip';
import { getInputBaseClassName } from '../InputBase';
import './Dropdown.scss';

export type DropdownItem = {
	label: string;
	value: string | number | boolean | null;
	className?: string;
	icon?: IconType<SelectItem>;
	title?: string;
	disabled?: boolean;
	tooltip?: string;
	data?: Record<string, any>;
};

type DropdownProps = Omit<DropdownPropsOriginal, 'options' | 'dropdownIcon' | 'optionLabel' | 'optionValue'> & {
	options?: DropdownItem[];
	inline?: boolean;
	withEmpty?: boolean;
	emptyLabel?: string;
	emptyClassName?: string | (() => string);
	emptyValue?: any;
	slim?: boolean;
	minimal?: boolean;
	variant?: 'default' | 'primary-outlined';
	invalid?: boolean;
};

export const Dropdown = forwardRef(
	(
		{
			className,
			value,
			onChange,
			defaultValue,
			inline,
			options,
			withEmpty = false,
			placeholder,
			emptyLabel,
			emptyClassName,
			emptyValue = null,
			'aria-label': ariaLabel,
			slim = false,
			minimal = false,
			variant = 'default',
			invalid = false,
			...restProps
		}: DropdownProps & { 'aria-label'?: string },
		ref: Ref<DropdownOriginal>
	) => {
		const { t } = useTranslation();
		const [currentValueTemp, setCurrentValue] = useState<string | number | null>(
			typeof value === 'undefined' ? defaultValue : value
		);
		const currentValue = currentValueTemp || -1;

		useEffect(() => {
			if (value !== currentValue) {
				setCurrentValue(value);
			}
		}, [value, currentValue]);

		const emptyOption = {
			value: emptyValue,
			label: emptyLabel || t('misc.dropdownPleaseSelect') || '',
			className: typeof emptyClassName === 'function' ? emptyClassName() : emptyClassName,
		};
		const finalOptions: DropdownItem[] = useMemo(
			() => (withEmpty ? [emptyOption, ...(options || [])] : options || []),
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[withEmpty, options]
		);

		return (
			<DropdownOriginal
				ref={ref}
				value={currentValue}
				defaultValue={defaultValue}
				aria-label={ariaLabel}
				onChange={(event) => {
					if (onChange) {
						onChange(event);
					} else {
						setCurrentValue(event.value);
					}
				}}
				valueTemplate={(item) => {
					if (item) {
						const optiondId = `item-${item?.value || item?.label}`;
						return (
							<>
								{item?.tooltip && (
									<Tooltip target={`#${optiondId}`} content={item.tooltip} position="bottom" />
								)}
								<span id={optiondId}>{item.label}</span>
							</>
						);
					}

					return placeholder || emptyLabel || t('misc.dropdownPleaseSelect');
				}}
				className={classNames(
					getInputBaseClassName({ inline, slim, variant, minimal, invalid }),
					'dropdown',
					className
				)}
				dropdownIcon="fa fa-solid fa-chevron-down"
				panelClassName="dropdown-panel"
				optionLabel="label"
				optionValue="value"
				{...restProps}
				options={finalOptions}
			/>
		);
	}
);
